import type { CalendarDate, CalendarMonth, CalendarSpan, CalendarSpanSegment } from './Calendar.types'
import { calendarDaysBetween } from './calendarDate'

export interface CalendarSpanLayout<TSpan = never> {
  /** The input months, with each week's `laneCount` filled in. */
  months: Array<CalendarMonth>
  /** Keyed by `${monthKey}:${weekIndex}`. */
  segmentsByWeek: ReadonlyMap<string, Array<CalendarSpanSegment<TSpan>>>
  /** Every segment touching a given day, for that day's render slot. */
  segmentsByDate: ReadonlyMap<CalendarDate, Array<CalendarSpanSegment<TSpan>>>
}

export function getWeekKey(monthKey: string, weekIndex: number): string {
  return `${monthKey}:${weekIndex}`
}

/** A span with no nights, which fills its only day rather than half of it. */
function isNightless(span: CalendarSpan<unknown>): boolean {
  return span.from === span.to
}

function overlaps(a: CalendarSpan<unknown>, b: CalendarSpan<unknown>): boolean {
  /*
   * Two stays changing over on the same day each take half of its cell, so
   * they can share a lane. A nightless stay cannot: it fills the whole cell,
   * and the exemption is not transitive — with a nightless stop between two
   * neighbours that already touch each other, all three pairs would be
   * exempted and all three bars would stack in one lane on that day.
   */
  const sharesOnlyBoundary = (a.to === b.from || b.to === a.from)
    && a.shareLaneAtBoundary
    && b.shareLaneAtBoundary
    && !isNightless(a)
    && !isNightless(b)
  if (sharesOnlyBoundary) return false

  return a.from <= b.to && b.from <= a.to
}

/**
 * Assigns each span a vertical lane, lowest free lane first.
 *
 * Lanes are assigned per span rather than per segment so a stay keeps the
 * same row when it wraps onto the following week. Sorting is fully
 * deterministic — start, then longest first, then id — so lanes do not
 * reshuffle when unrelated data changes.
 */
export function assignSpanLanes<TSpan>(
  spans: ReadonlyArray<CalendarSpan<TSpan>>,
): ReadonlyMap<string, number> {
  const ordered = [...spans]
    .filter(span => span.from <= span.to)
    .sort((a, b) => {
      const laneOrderDifference = (a.laneOrder ?? 0) - (b.laneOrder ?? 0)
      if (laneOrderDifference !== 0) return laneOrderDifference
      if (a.from !== b.from) return a.from < b.from ? -1 : 1
      const aLength = calendarDaysBetween(a.from, a.to)
      const bLength = calendarDaysBetween(b.from, b.to)
      if (aLength !== bLength) return bLength - aLength
      return a.id < b.id ? -1 : 1
    })

  const lanes = new Map<string, number>()
  const occupants: Array<Array<CalendarSpan<TSpan>>> = []

  for (const span of ordered) {
    let lane = occupants.findIndex(inLane => !inLane.some(other => overlaps(span, other)))
    if (lane === -1) {
      lane = occupants.length
      occupants.push([])
    }
    occupants[lane].push(span)
    lanes.set(span.id, lane)
  }

  return lanes
}

/**
 * Clips every span against every week, producing one segment per week the
 * span touches. A stay running Thursday to the following Tuesday is two
 * segments, not one — which is the whole reason this module exists.
 */
export function buildSpanLayout<TSpan>(
  months: ReadonlyArray<CalendarMonth>,
  spans: ReadonlyArray<CalendarSpan<TSpan>> = [],
): CalendarSpanLayout<TSpan> {
  if (spans.length === 0) {
    return { months: [...months], segmentsByWeek: new Map(), segmentsByDate: new Map() }
  }

  const lanes = assignSpanLanes(spans)
  const usable = spans.filter(span => span.from <= span.to && lanes.has(span.id))

  const segmentsByWeek = new Map<string, Array<CalendarSpanSegment<TSpan>>>()
  const segmentsByDate = new Map<CalendarDate, Array<CalendarSpanSegment<TSpan>>>()
  /** Every rendered segment of a span, in render order, to pick a label from. */
  const segmentsBySpan = new Map<string, Array<CalendarSpanSegment<TSpan>>>()

  const laidOutMonths = months.map((month): CalendarMonth => ({
    ...month,
    weeks: month.weeks.map((week, weekIndex) => {
      const weekStart = week.days[0].date
      const segments: Array<CalendarSpanSegment<TSpan>> = []
      let laneCount = 0

      /*
       * Clip to the month's own days, not the week's. A week at either end of
       * a month is padded with the neighbouring month's dates, which render
       * blank — drawing a bar across them would colour the 1st of October on
       * September's grid as well as its own.
       */
      const ownDays = week.days.filter(day => !day.isAdjacentMonth)
      if (ownDays.length === 0) return { ...week, laneCount }
      const clipStart = ownDays[0].date
      const clipEnd = ownDays[ownDays.length - 1].date

      for (const span of usable) {
        const overlapStart = span.from > clipStart ? span.from : clipStart
        const overlapEnd = span.to < clipEnd ? span.to : clipEnd
        if (overlapStart > overlapEnd) continue

        const lane = lanes.get(span.id) ?? 0
        const segment: CalendarSpanSegment<TSpan> = {
          span,
          weekIndex,
          columnStart: calendarDaysBetween(weekStart, overlapStart),
          columnSpan: calendarDaysBetween(overlapStart, overlapEnd) + 1,
          lane,
          isStart: overlapStart === span.from,
          isEnd: overlapEnd === span.to,
          isWidest: false,
        }

        segments.push(segment)
        const forSpan = segmentsBySpan.get(span.id)
        if (forSpan) forSpan.push(segment)
        else segmentsBySpan.set(span.id, [segment])

        laneCount = Math.max(laneCount, lane + 1)

        for (let offset = 0; offset < segment.columnSpan; offset++) {
          const day = week.days[segment.columnStart + offset]
          if (!day) continue
          const existing = segmentsByDate.get(day.date)
          if (existing) {
            existing.push(segment)
          } else {
            segmentsByDate.set(day.date, [segment])
          }
        }
      }

      if (segments.length > 0) {
        segmentsByWeek.set(getWeekKey(month.key, weekIndex), segments)
      }

      return { ...week, laneCount }
    }),
  }))

  /*
   * Months and weeks are walked in render order above, so the first segment
   * matching the widest width is also the earliest one on screen.
   */
  for (const spanSegments of segmentsBySpan.values()) {
    let widest = spanSegments[0]
    for (const segment of spanSegments) {
      if (segment.columnSpan > widest.columnSpan) widest = segment
    }
    widest.isWidest = true
  }

  return { months: laidOutMonths, segmentsByWeek, segmentsByDate }
}
